import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Stránka, ktorá sa stiahne až vtedy, keď na ňu človek naozaj príde.
 *
 * Bez toho bol celý web jeden súbor: 1,2 MB, z toho kanbanová doska, testy,
 * chat aj grafy — teda veci, ktoré pri otvorení prehľadu nikto nepotrebuje,
 * ale na mobile sa za ne platí pri každom prvom načítaní.
 *
 * Poistka na koniec je dôležitejšia než samotné delenie. Pri nasadení sa mení
 * názov súboru s kódom. Kto má appku otvorenú spred nasadenia a klepne na
 * záložku, siahne po súbore, ktorý na serveri už nie je — server vráti
 * index.html (tak funguje SPA), prehliadač to nevie načítať ako modul a človek
 * dostane prázdno namiesto stránky. Predtým sa to stať nemohlo, lebo všetok kód
 * prišiel naraz pri načítaní.
 *
 * Jedno obnovenie stránky to spraví, tak sa spraví samo. Príznak v sessionStorage
 * drží, aby sa appka nezacyklila, keď je chyba niekde inde.
 *
 * Pod service workerom to ale obnovenie nespraví: worker vráti svoj vlastný
 * uložený index, appka sa načíta v tej istej generácii a siahne po tom istom
 * chýbajúcom súbore. Tam sa preto neobnovuje a chyba ide ďalej ako
 * `StaleChunkError` — ErrorBoundary na ňu má obrazovku s tlačidlom, ktoré
 * generáciu naozaj vymení.
 */

const RELOAD_FLAG = 'whoworks:chunk-reload';

/**
 * "Súbor na serveri nie je" verzus "v stránke je chyba". Rozoznáva sa podľa
 * hlášky, lebo spoločný typ na to neexistuje a každý prehliadač ju píše inak:
 * Chrome "Failed to fetch dynamically imported module", Firefox "error loading
 * dynamically imported module", Safari "Importing a module script failed".
 */
const STALE_CHUNK_MESSAGE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

/** Obrazovka patrí ku generácii, ktorú server už neservíruje. */
export class StaleChunkError extends Error {
  constructor(cause: unknown) {
    super('Táto obrazovka patrí k inej verzii aplikácie.', { cause });
    this.name = 'StaleChunkError';
  }
}

/** Návratový typ je `boolean`, nie predikát: StaleChunkError sa od Error
 *  typovo nelíši ničím, takže by TypeScript v zápornej vetve zúžil Error na
 *  `never` a hláška by sa z nej už nedala prečítať. */
export const isStaleChunkError = (error: unknown): boolean =>
  error instanceof StaleChunkError;

/** Uložený index workera prežije aj obnovenie, takže reload nikam nevedie. */
const servedByServiceWorker = (): boolean =>
  'serviceWorker' in navigator && Boolean(navigator.serviceWorker.controller);

/** sessionStorage vie hodiť (súkromné okno, zakázané úložisko). Nič z toho
 *  nesmie zhodiť načítanie stránky, takže sa číta aj píše opatrne. */
const readFlag = (): boolean => {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (value: boolean): void => {
  try {
    if (value) {
      window.sessionStorage.setItem(RELOAD_FLAG, '1');
    } else {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    }
  } catch {
    // Bez úložiska sa poistka zredukuje na "skús obnoviť vždy". Stále lepšie
    // než prázdna obrazovka.
  }
};

/**
 * Stiahne súbor s kódom a pri neúspechu sa zachová podľa toho, čo je vyššie:
 * pod workerom `StaleChunkError`, inak jedno obnovenie stránky.
 */
const loadGuarded = async <M>(load: () => Promise<M>): Promise<M> => {
  try {
    const module = await load();
    // Načítalo sa — predošlý neúspech už neplatí.
    writeFlag(false);
    return module;
  } catch (error) {
    if (
      error instanceof Error &&
      STALE_CHUNK_MESSAGE.test(error.message) &&
      servedByServiceWorker()
    ) {
      throw new StaleChunkError(error);
    }
    if (readFlag()) {
      // Obnovenie sme už skúsili a nepomohlo. Ďalšie by bola slučka, takže
      // nech chyba prejde ďalej a je ju aspoň vidieť.
      throw error;
    }
    writeFlag(true);
    window.location.reload();
    // Stránka odchádza; toto sa zámerne nikdy nedokončí, aby medzitým ostalo
    // na obrazovke načítavanie a nie zábleskchyby.
    return new Promise<M>(() => {});
  }
};

export const lazyPage = <M extends Record<string, unknown>>(
  load: () => Promise<M>,
  name: keyof M & string,
): LazyExoticComponent<ComponentType> =>
  lazy(async () => {
    const module = await loadGuarded(load);
    return { default: module[name] as ComponentType };
  });

/**
 * To isté pre komponent s props — okno hľadania, asistent, list stavu. Tie sú
 * v ráme appky, ale otvárajú sa až na klepnutie, takže nemusia byť v súbore,
 * na ktorý čaká prvé vykreslenie. Poistka pri chýbajúcom súbore je rovnaká ako
 * pri stránkach: bez nej by sa po nasadení namiesto okna ukázala chyba.
 */
// `any` preto, lebo props každého komponentu sú iné a typ si ich nesie ďalej v M[K].
export const lazyComponent = <M extends Record<K, ComponentType<any>>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
): LazyExoticComponent<M[K]> =>
  lazy(async () => {
    const module = await loadGuarded(load);
    return { default: module[name] };
  });
